import React, { forwardRef, useId, useState } from "react";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import { MelaErrorText } from "./MelaErrorText";
import "./MelaInput.css";
import type {
  MelaInputProps,
  MelaInputSize,
  MelaTextAreaProps,
} from "./MelaInput.types";

const DEFAULT_SIZE = "md";

const getSizeConfig = (size: MelaInputSize) => {
  switch (size) {
    case "xxs":
      return {
        height: 28,
        padding: "0 8px",
        fontSize: "0.75rem",
      };
    case "xs":
      return {
        height: 32,
        padding: "0 10px",
        fontSize: "0.82rem",
      };
    case "sm":
      return {
        height: 36,
        padding: "0 10px",
        fontSize: "0.82rem",
      };
    case "md":
    default:
      return {
        height: 40,
        padding: "0 12px",
        fontSize: "0.88rem",
      };
  }
};

type MelaInputCssVariables = React.CSSProperties & {
  ["--mela-input-container-width"]?: string;
  ["--mela-input-height"]?: string;
  ["--mela-input-padding"]?: string;
  ["--mela-input-font-size"]?: string;
};

export const MelaInput = forwardRef<HTMLInputElement, MelaInputProps>(
  (
    {
      label,
      helperText,
      error = false,
      errorText,
      errorDataTestId,
      success = false,
      successText,
      successDataTestId,
      startIcon,
      endIcon,
      size = DEFAULT_SIZE,
      inputElement,
      fullWidth = false,
      disabled = false,
      hideArrows = false,
      className = "",
      id: providedId,
      style,
      inputStyle,
      chromeClassName,
      chromeStyle,
      containerClassName,
      containerRef,
      inputRef,
      borderless = false,
      onFocus,
      onBlur,
      value,
      defaultValue,
      ...props
    },
    ref,
  ) => {
    const autoId = useId();
    const inputId = providedId || autoId;
    const helperTextId = `${inputId}-helper`;
    const errorTextId = `${inputId}-error`;
    const successTextId = `${inputId}-success`;
    const sizeConfig = getSizeConfig(size);

    const [isFocused, setIsFocused] = useState(false);
    const hasValue = !!value || !!defaultValue;
    const isFilled = !isFocused && hasValue;

    const containerStyle: MelaInputCssVariables = {
      "--mela-input-container-width": fullWidth ? "100%" : "auto",
      "--mela-input-height": `${sizeConfig.height}px`,
      "--mela-input-padding": sizeConfig.padding,
      "--mela-input-font-size": sizeConfig.fontSize,
      ...style,
    };

    const computedChromeClassName = [
      "mela-input__chrome",
      error && "mela-input__chrome--error",
      success && !error && "mela-input__chrome--success",
      isFilled && "mela-input__chrome--filled",
      disabled && "mela-input__chrome--disabled",
      borderless && "mela-input__chrome--borderless",
      chromeClassName,
    ]
      .filter(Boolean)
      .join(" ");

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    // Filter out non-DOM props that may leak from bridge wrappers
    const {
      minRows: _minRows,
      maxRows: _maxRows,
      multiline: _multiline,
      rows: _rows,
      showInputStyleFocused: _showInputStyleFocused,
      hideControls: _hideControls,
      InputLabelProps: _InputLabelProps,
      ...domProps
    } = props as any;

    const inputElementProps = {
      ref: inputRef || ref,
      id: inputId,
      disabled,
      className: `mela-input__element ${hideArrows ? "mela-input__element--no-arrows" : ""} ${className}`,
      style: inputStyle,
      "aria-invalid": error || undefined,
      "aria-describedby": error
        ? errorTextId
        : success && successText
          ? successTextId
          : helperText
            ? helperTextId
            : undefined,
      onFocus: handleFocus,
      onBlur: handleBlur,
      value: value ?? undefined,
      defaultValue,
      ...domProps,
    };

    const inputNode = inputElement ? (
      React.cloneElement(inputElement, inputElementProps as any)
    ) : (
      <input {...inputElementProps} />
    );

    return (
      <div
        data-mela-input-container="true"
        style={containerStyle}
        className={containerClassName}
        ref={containerRef}
      >
        {label && (
          <label htmlFor={inputId} className="mela-input__label">
            {label}
          </label>
        )}
        <div className={computedChromeClassName} style={chromeStyle}>
          {startIcon && <span className="mela-input__icon">{startIcon}</span>}
          {inputNode}
          {endIcon && <span className="mela-input__icon">{endIcon}</span>}
        </div>
        <MelaErrorText
          id={errorTextId}
          errorText={errorText}
          dataTestId={errorDataTestId}
        />
        {!error && success && successText ? (
          <span
            id={successTextId}
            className="mela-input__success-text"
            data-testid={successDataTestId}
          >
            {successText}
          </span>
        ) : helperText ? (
          <span id={helperTextId} className="mela-input__helper-text">
            {helperText}
          </span>
        ) : null}
      </div>
    );
  },
);

MelaInput.displayName = "MelaInput";

export const MelaTextArea = forwardRef<HTMLTextAreaElement, MelaTextAreaProps>(
  (
    {
      label,
      helperText,
      error = false,
      errorText,
      errorDataTestId,
      success = false,
      successText,
      successDataTestId,
      size = DEFAULT_SIZE,
      fullWidth = false,
      disabled = false,
      minRows = 3,
      maxRows = 6,
      className = "",
      id: providedId,
      style,
      inputStyle,
      chromeClassName,
      chromeStyle,
      containerClassName,
      containerRef,
      inputRef,
      borderless = false,
      onFocus,
      onBlur,
      value,
      defaultValue,
      ...props
    },
    ref,
  ) => {
    const autoId = useId();
    const inputId = providedId || autoId;
    const helperTextId = `${inputId}-helper`;
    const errorTextId = `${inputId}-error`;
    const successTextId = `${inputId}-success`;
    const sizeConfig = getSizeConfig(size);

    const [isFocused, setIsFocused] = useState(false);
    const hasValue = !!value || !!defaultValue;
    const isFilled = !isFocused && hasValue;

    const containerStyle: MelaInputCssVariables = {
      "--mela-input-container-width": fullWidth ? "100%" : "auto",
      "--mela-input-padding": sizeConfig.padding,
      "--mela-input-font-size": sizeConfig.fontSize,
      ...style,
    };

    const computedChromeClassName = [
      "mela-input__chrome",
      "mela-input__chrome--multiline",
      error && "mela-input__chrome--error",
      success && !error && "mela-input__chrome--success",
      isFilled && "mela-input__chrome--filled",
      disabled && "mela-input__chrome--disabled",
      borderless && "mela-input__chrome--borderless",
      chromeClassName,
    ]
      .filter(Boolean)
      .join(" ");

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    // Filter out non-DOM props that may leak from bridge wrappers
    const {
      showInputStyleFocused: _showInputStyleFocused,
      disableStyleFocused: _disableStyleFocused,
      hideControls: _hideControls,
      multiline: _multiline,
      rows: _rows,
      InputLabelProps: _InputLabelProps,
      ...domProps
    } = props as any;

    return (
      <div
        data-mela-input-container="true"
        style={containerStyle}
        className={containerClassName}
        ref={containerRef}
      >
        {label && (
          <label htmlFor={inputId} className="mela-input__label">
            {label}
          </label>
        )}
        <div className={computedChromeClassName} style={chromeStyle}>
          <TextareaAutosize
            {...domProps}
            ref={inputRef || ref}
            id={inputId}
            disabled={disabled}
            minRows={minRows}
            maxRows={maxRows}
            onFocus={handleFocus}
            onBlur={handleBlur}
            value={value ?? undefined}
            defaultValue={defaultValue}
            className={`mela-input__element mela-input__textarea ${className}`}
            style={inputStyle}
            aria-invalid={error || undefined}
            aria-describedby={
              error
                ? errorTextId
                : success && successText
                  ? successTextId
                  : helperText
                    ? helperTextId
                    : undefined
            }
          />
        </div>
        <MelaErrorText
          id={errorTextId}
          errorText={errorText}
          dataTestId={errorDataTestId}
        />
        {!error && success && successText ? (
          <span
            id={successTextId}
            className="mela-input__success-text"
            data-testid={successDataTestId}
          >
            {successText}
          </span>
        ) : helperText ? (
          <span id={helperTextId} className="mela-input__helper-text">
            {helperText}
          </span>
        ) : null}
      </div>
    );
  },
);

MelaTextArea.displayName = "MelaTextArea";
